import { useEffect, useMemo, useRef, useState } from "react";
import { useTerm } from "@/lib/terminology.jsx";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Search, ChevronDown } from "lucide-react";

/**
 * v5.13-r2 DealMultiPicker — shared chip picker for Related Deals.
 *
 * Behaviour (per user spec):
 *  - Three ways to add a deal, all working together:
 *      1) Type-to-search  (existing)
 *      2) Focus / click the field with no query → dropdown shows all active
 *         deals for the current pipeline (NEW).
 *      3) "Select All Deals in [Pipeline]" shortcut button (existing).
 *  - Clicking a dropdown row ALWAYS adds a chip and keeps the dropdown open,
 *    so multiple deals can be picked in one interaction. (Prev: onClick was
 *    eaten by input's onBlur → dropdown vanished, click was lost.)
 *
 * Props:
 *  - selected: [{deal_id, company_name, pipeline_id}, ...]
 *  - onChange(selected)
 *  - fallbackPipelineId: string  — pipeline to source deals from when
 *      no deal is selected yet.
 *  - testidPrefix?: string
 */
export default function DealMultiPicker({
  selected,
  onChange,
  fallbackPipelineId = null,
  testidPrefix = "task-deals",
}) {
  const term = useTerm();
  const { pipelines, pipelineId: ctxPipelineId } = usePipelineCtx();

  const [query, setQuery] = useState("");
  const [allDeals, setAllDeals] = useState([]);  // active deals for current pipeline
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const justPickedRef = useRef(false);  // suppresses outside-click auto-close right after a pick

  const pipelineForShortcut =
    selected[0]?.pipeline_id || fallbackPipelineId || ctxPipelineId;

  const pipelineName = useMemo(() => {
    const p = pipelines.find((x) => x.pipeline_id === pipelineForShortcut);
    return p?.pipeline_name || term("Pipeline");
  }, [pipelines, pipelineForShortcut, term]);

  // Load all active deals for the current pipeline once on mount/pipeline change
  useEffect(() => {
    if (!pipelineForShortcut) {
      setAllDeals([]);
      return;
    }
    setLoading(true);
    api
      .get(`/v513/pipelines/${pipelineForShortcut}/active-deals`)
      .then(({ data }) => setAllDeals(data))
      .catch(() => setAllDeals([]))
      .finally(() => setLoading(false));
  }, [pipelineForShortcut]);

  // Close dropdown when clicking outside the whole picker
  useEffect(() => {
    const onDoc = (e) => {
      // If a pick just happened, do not treat the same event as an outside click
      if (justPickedRef.current) {
        justPickedRef.current = false;
        return;
      }
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const selectedIds = new Set(selected.map((s) => s.deal_id));
    const q = query.trim().toLowerCase();
    return allDeals
      .filter((d) => !selectedIds.has(d.deal_id))
      .filter((d) => (q ? d.company_name.toLowerCase().includes(q) : true))
      .slice(0, 100);
  }, [allDeals, selected, query]);

  const addDeal = (d) => {
    if (selected.some((s) => s.deal_id === d.deal_id)) return;
    justPickedRef.current = true;  // suppresses the outside-click handler that fires on this same mousedown
    onChange([...selected, d]);
    setQuery("");
    setOpen(true);
    // Keep focus so subsequent picks flow naturally
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeDeal = (id) => {
    onChange(selected.filter((d) => d.deal_id !== id));
  };

  const selectAll = () => {
    if (!pipelineForShortcut) {
      toast.error(`No ${term("Pipeline").toLowerCase()} to select from`);
      return;
    }
    const existing = new Set(selected.map((d) => d.deal_id));
    const additions = allDeals.filter((d) => !existing.has(d.deal_id));
    onChange([...selected, ...additions]);
    toast.success(
      `Added ${additions.length} ${term("Deals").toLowerCase()} from ${pipelineName}`
    );
  };

  const border = { borderColor: "var(--cw-border)" };

  return (
    <div className="mt-1 space-y-2" ref={containerRef}>
      {pipelineForShortcut && (
        <button
          type="button"
          onClick={selectAll}
          className="text-xs text-[var(--cw-primary)] hover:underline"
          data-testid={`${testidPrefix}-select-all`}
        >
          Select All {term("Deals")} in {pipelineName}
        </button>
      )}

      {/* Selected chips */}
      <div
        className="min-h-[36px] px-2 py-1.5 rounded-md border flex flex-wrap gap-1.5"
        style={border}
        data-testid={`${testidPrefix}-chips`}
      >
        {selected.length === 0 && (
          <div className="text-xs text-[var(--cw-muted)] py-1">
            None selected — this is fine.
          </div>
        )}
        {selected.map((d) => (
          <span
            key={d.deal_id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs"
            style={{
              background: "rgba(40,42,116,0.08)",
              color: "var(--cw-primary)",
            }}
            data-testid={`${testidPrefix}-chip-${d.deal_id}`}
          >
            {d.company_name}
            <button
              type="button"
              onClick={() => removeDeal(d.deal_id)}
              data-testid={`${testidPrefix}-chip-remove-${d.deal_id}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>

      {/* Search input + dropdown */}
      <div className="relative">
        <div
          className="flex items-center gap-1 h-9 px-2 rounded-md border cursor-text"
          style={border}
          onClick={() => {
            setOpen(true);
            inputRef.current?.focus();
          }}
        >
          <Search size={14} className="text-[var(--cw-muted)]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={`Type to search or click to browse ${term("deals").toLowerCase()}…`}
            className="flex-1 outline-none text-sm bg-transparent"
            data-testid={`${testidPrefix}-search`}
          />
          <ChevronDown size={14} className="text-[var(--cw-muted)]" />
        </div>
        {open && (
          <div
            className="absolute z-10 mt-1 w-full rounded-md border bg-white shadow-md max-h-64 overflow-auto"
            style={border}
            data-testid={`${testidPrefix}-dropdown`}
          >
            {loading && (
              <div className="px-3 py-2 text-xs text-[var(--cw-muted)]">
                Loading {term("deals").toLowerCase()}…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-[var(--cw-muted)]">
                {query
                  ? "No matches."
                  : selected.length === allDeals.length
                    ? `All ${term("deals").toLowerCase()} already selected.`
                    : `No active ${term("deals").toLowerCase()} in ${pipelineName}.`}
              </div>
            )}
            {!loading &&
              filtered.map((d) => (
                <button
                  key={d.deal_id}
                  type="button"
                  /* onMouseDown fires BEFORE input's onBlur, so the input's blur
                     never gets to close this dropdown before the click. This is
                     the fix for the "clicking a deal does nothing" bug. */
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addDeal(d);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-[rgba(40,42,116,0.05)]"
                  data-testid={`${testidPrefix}-option-${d.deal_id}`}
                >
                  {d.company_name}
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
